import React, { useState } from 'react'
import {
  Chip,
  Select,
  MenuItem,
  FormControl,
  Box,
  IconButton,
  CircularProgress,
  Tooltip
} from '@mui/material'
import { Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material'
import { fpStatusColor, fpStatusChipSx, type FPStatus } from './FpStatusChip'

// Re-exported so existing importers keep `import { FPStatus } from './EditableStatusChip'`.
export type { FPStatus }

interface EditableStatusChipProps {
  currentStatus: FPStatus
  fpCode: string
  onStatusUpdate: (fpCode: string, newStatus: FPStatus) => Promise<void>
  disabled?: boolean
}

const STATUS_OPTIONS: FPStatus[] = [
  'Onboarding',
  'Active',
  'Terminated',
  'Test'
]

export const EditableStatusChip: React.FC<EditableStatusChipProps> = ({
  currentStatus,
  fpCode,
  onStatusUpdate,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<FPStatus>(currentStatus)
  const [isLoading, setIsLoading] = useState(false)

  const handleEditClick = () => {
    if (disabled) return
    setIsEditing(true)
    setSelectedStatus(currentStatus)
  }

  const handleSave = async () => {
    if (selectedStatus === currentStatus) {
      setIsEditing(false)
      return
    }

    setIsLoading(true)
    try {
      await onStatusUpdate(fpCode, selectedStatus)
      setIsEditing(false)
    } catch (error) {
      // Reset to current status on error
      setSelectedStatus(currentStatus)
      console.error('Failed to update status:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setSelectedStatus(currentStatus)
    setIsEditing(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSave()
    } else if (event.key === 'Escape') {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value as FPStatus)}
            onKeyDown={handleKeyDown}
            autoFocus
            disabled={isLoading}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display="flex" gap={0.5}>
          <Tooltip title="Save">
            <IconButton
              size="small"
              onClick={handleSave}
              disabled={isLoading}
              color="primary"
            >
              {isLoading ? <CircularProgress size={16} /> : <CheckIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title="Cancel">
            <IconButton
              size="small"
              onClick={handleCancel}
              disabled={isLoading}
            >
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    )
  }

  return (
    <Chip
      label={currentStatus}
      size="small"
      color={fpStatusColor(currentStatus)}
      variant="filled"
      onClick={!disabled ? handleEditClick : undefined}
      sx={{
        ...fpStatusChipSx(currentStatus),
        cursor: !disabled ? 'pointer' : 'default',
        '&:hover': !disabled ? { opacity: 0.8 } : {},
      }}
    />
  )
}
